<template>
  <!-- The main container for the minimized card. It's positioned fixed at the bottom-right of the screen.  -->
  <div
    :class="[
      $style.minimisedCardContainer,
      { [$style.behindConfirmationDialog]: isConfirmationDialogActive },
    ]"
  >
    <!-- Container for text content (title and description) -->
    <div :class="$style.textContainer">
      <!-- The modal title. The span potentially handles text overflow -->
      <span :class="$style.modalTitle">{{ modalTitle }}</span>

      <!-- Modal content -->
      <span v-if="modalContent" :class="$style.modalContent">{{
        modalContent
      }}</span>
    </div>
    <!-- Container for action buttons / icons -->
    <div :class="$style.actions">
      <!-- Restore button:
         - Emits 'restore' event when clicked
         - The parent (InteractiveModal.vue) will catch this and re-emit it -->
      <button
        type="button"
        :class="$style.actionButton"
        aria-label="restore modal"
        @click="$emit('restore')"
      >
        <i class="mdi mdi-arrow-expand"></i>
      </button>

      <!-- Close button: 
        - Emits 'close' event when clicked
        - The parent (InteractiveModal.vue) will catch this and re-emit it -->
      <button
        type="button"
        :class="$style.actionButton"
        aria-label="close modal"
        @click="$emit('close')"
      >
        <i class="mdi mdi-close"></i>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { State } from 'vuex-class';

@Component
export default class MinimisedCard extends Vue {
  @Prop({ type: String, required: true })
  public modalTitle!: string;

  /**
   * Default empty string ensures we always have a valid value,
   * preventing errors when null/undefined is passed.
   */
  @Prop({ type: String, default: '' })
  public modalContent!: string;

  @State((state) => state.unsavedChangesGuard.isUnsavedChangesDialogActive)
  public isConfirmationDialogActive!: boolean;
}
</script>

<style lang="scss" module>
.minimisedCardContainer {
  // Positioning: Bottom right of the viewport
  position: fixed;
  bottom: -10px;
  right: 150px;

  // Appearance: Card-like look
  border: 1px solid $grey-2;
  background-color: $white;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 0 1px 0 rgba($black-1, 0.4), 0 16px 32px rgba($black-1, 0.2);
  padding: 8px 20px 18px 20px;

  // Layout to arrange title and actions horizontally
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: space-between;

  // Ensure it stays on top of other content
  z-index: 38;
  width: 380px;
  height: 84px;

  &:hover {
    cursor: pointer;
    background-color: $grey-light-5;
  }
}

.behindConfirmationDialog {
  // Lower z-index to be behind Buefy's modal overlay (typically z-index 40)
  z-index: 39 !important; // Use !important to ensure override if needed
  pointer-events: none !important; // Disable interactions
}

.textContainer {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.modalTitle {
  font-size: $size-4;
  font-weight: $weight-semibold;
  color: $primary;
  white-space: nowrap; // Prevent title wrapping
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px; // Limit width to prevent pushing buttons too far
}

.modalContent {
  font-size: $size-5;
  color: $grey;
  margin-top: 4px; // Space between title and description
  white-space: nowrap; // Keep description on one line
  overflow: hidden; // Hide overflow
  text-overflow: ellipsis; // Add '...' for overflow
  max-width: 200px; // Limit width, same as title or adjust as needed
}

.actions {
  display: flex;
  align-items: center;
  gap: 8px; // Space between action buttons
  // Prevent action buttons from shrinking
  flex-shrink: 0;
}

.actionButton {
  background: none;
  border: none;
  padding: 4px;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $dark;
  font-size: $size-2;

  &:hover {
    color: $black;
  }

  // Consider adding focus styles for accessibility
  &:focus {
    outline: 1px dotted $black;
    outline-offset: 2px;
  }

  > i {
    // Target the icon directly if needed
    display: block; // Ensures icon behaves predictably
  }
}
</style>
